import { useEffect, useRef, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useHotelHub } from "../../../providers/signalr/HotelHubProvider";
import { useSignalR } from "../../../providers/signalr/SignalRProvider";
import { queryKeys } from "../../../lib/react-query/queryClient";
import { logger } from "../../../utils/logger";
// Express Checkout removed: no checkout query invalidation

/**
 * Hook to connect SignalR events to React Query cache invalidation
 * This ensures real-time updates trigger data refetches
 */
export const useSignalRQueryInvalidation = () => {
  const queryClient = useQueryClient();
  const { on, off, isConnected } = useHotelHub();
  const { onReconnected } = useSignalR();
  const orderInvalidateTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Reconnect catch-up: invalidate ALL cached data when connection is restored
  useEffect(() => {
    const hubUrl = "/hub";
    const unsubscribe = onReconnected(hubUrl, () => {
      logger.log("SignalR reconnected — invalidating all cached data to catch up");
      const keysToInvalidate = [
        // Note: bootstrap is intentionally excluded — it has a 5-minute staleTime,
        // rarely changes, and invalidating it during reconnect can trigger AdminBootstrapGate
        // to flash the loading overlay if React Query briefly reports isLoading.
        queryKeys.orders(),
        queryKeys.orderCounts(),
        queryKeys.serviceRequests(),
        queryKeys.serviceRequestCounts(),
        queryKeys.lateCheckouts(),
        queryKeys.lateCheckoutCounts(),
        queryKeys.transferRequests(),
        queryKeys.transferRequestCounts(),
        queryKeys.checkins(),
        queryKeys.notificationCounts(),
        queryKeys.dashboardActivity(),
        queryKeys.dashboardStats(),
        queryKeys.queue(),
        queryKeys.housekeeping(),
        queryKeys.chat(),
        queryKeys.unifiedRequests(),
        queryKeys.frontDeskBoard(),
        queryKeys.kitchenBoard(),
      ];
      keysToInvalidate.forEach((key) => {
        queryClient.invalidateQueries({ queryKey: key, exact: false });
      });
    });
    return unsubscribe;
  }, [onReconnected, queryClient]);

  const invalidateOrdersDebounced = useCallback(() => {
    // Trailing-edge debounce: always delay a bit and reset timer on each event
    if (orderInvalidateTimerRef.current) {
      clearTimeout(orderInvalidateTimerRef.current);
    }
    orderInvalidateTimerRef.current = setTimeout(() => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.orders(),
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.orderCounts(),
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.dashboardActivity(),
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.dashboardStats(),
        exact: false,
      });
      // Also invalidate unified requests
      queryClient.invalidateQueries({
        queryKey: queryKeys.unifiedRequests(),
        exact: false,
      });
      // Invalidate kitchen display board
      queryClient.invalidateQueries({
        queryKey: queryKeys.kitchenBoard(),
        exact: false,
      });
      orderInvalidateTimerRef.current = null;
    }, 500);
  }, [queryClient]);

  useEffect(() => {
    if (!isConnected) return;

    // Helper: invalidate front desk board (departure counts, arrival room status)
    const invalidateFrontDeskBoard = () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.frontDeskBoard(),
        exact: false,
      });
    };

    // Order-related events
    const handleNewOrder = () => {
      invalidateOrdersDebounced();
      invalidateFrontDeskBoard();
    };

    const handleOrderCountUpdate = () => {
      invalidateOrdersDebounced();
    };

    // Order status update (triggered when any admin changes order status)
    const handleOrderStatusUpdate = () => {
      invalidateOrdersDebounced();
      invalidateFrontDeskBoard();
    };

    // Service request events
    const handleNewServiceRequest = () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.serviceRequests(),
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.serviceRequestCounts(),
        exact: false,
      });
      // Update dashboard activity
      queryClient.invalidateQueries({
        queryKey: queryKeys.dashboardActivity(),
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.dashboardStats(),
        exact: false,
      });
      // Also invalidate unified requests
      queryClient.invalidateQueries({
        queryKey: queryKeys.unifiedRequests(),
        exact: false,
      });
      invalidateFrontDeskBoard();
    };

    const handleServiceRequestCountUpdate = () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.serviceRequests(),
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.serviceRequestCounts(),
        exact: false,
      });
      // Update dashboard stats
      queryClient.invalidateQueries({
        queryKey: queryKeys.dashboardStats(),
        exact: false,
      });
      // Also invalidate unified requests
      queryClient.invalidateQueries({
        queryKey: queryKeys.unifiedRequests(),
        exact: false,
      });
      invalidateFrontDeskBoard();
    };

    // Late checkout events
    const handleNewLateCheckout = () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.lateCheckouts(),
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.lateCheckoutCounts(),
        exact: false,
      });
      // Update dashboard activity
      queryClient.invalidateQueries({
        queryKey: queryKeys.dashboardActivity(),
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.dashboardStats(),
        exact: false,
      });
      // Also invalidate unified requests
      queryClient.invalidateQueries({
        queryKey: queryKeys.unifiedRequests(),
        exact: false,
      });
      invalidateFrontDeskBoard();
    };

    const handleLateCheckoutCountUpdate = () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.lateCheckoutCounts(),
        exact: false,
      });
      // Update dashboard stats
      queryClient.invalidateQueries({
        queryKey: queryKeys.dashboardStats(),
        exact: false,
      });
      // Also invalidate unified requests
      queryClient.invalidateQueries({
        queryKey: queryKeys.unifiedRequests(),
        exact: false,
      });
      invalidateFrontDeskBoard();
    };

    // Transfer request events
    const handleNewTransferRequest = () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.transferRequests(),
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.transferRequestCounts(),
        exact: false,
      });
      // Update dashboard activity
      queryClient.invalidateQueries({
        queryKey: queryKeys.dashboardActivity(),
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.dashboardStats(),
        exact: false,
      });
      // Also invalidate unified requests
      queryClient.invalidateQueries({
        queryKey: queryKeys.unifiedRequests(),
        exact: false,
      });
      invalidateFrontDeskBoard();
    };

    const handleTransferRequestCountUpdate = () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.transferRequestCounts(),
        exact: false,
      });
      // Update dashboard stats
      queryClient.invalidateQueries({
        queryKey: queryKeys.dashboardStats(),
        exact: false,
      });
      // Also invalidate unified requests
      queryClient.invalidateQueries({
        queryKey: queryKeys.unifiedRequests(),
        exact: false,
      });
      invalidateFrontDeskBoard();
    };

    // Check-ins events
    const handleNewCheckin = () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.checkins(),
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.frontDeskBoard(),
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.dashboardActivity(),
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.dashboardStats(),
        exact: false,
      });
    };
    const handleCheckinCountUpdate = () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.checkins(),
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.notificationCounts(),
        exact: false,
      });
    };

    // Housekeeping events
    const handleHousekeepingUpdate = () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.housekeeping(),
        exact: false,
      });
      invalidateFrontDeskBoard();
    };

    // Queue events
    const handleQueueUpdate = () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.queue(),
        exact: false,
      });
    };

    // PMS posting updates (orders, transfers, service requests)
    const handlePmsPostingUpdate = (
      resourceType: string,
      resourceId: string,
      status: string,
      lastError?: string | null,
    ) => {
      const rt = (resourceType || "").toLowerCase();
      const st = (status || "").toLowerCase();
      if (rt === "order") {
        queryClient.invalidateQueries({
          queryKey: queryKeys.orders(),
          exact: false,
        });
      } else if (rt === "transfer") {
        queryClient.invalidateQueries({
          queryKey: queryKeys.transferRequests(),
          exact: false,
        });
      } else if (rt === "servicerequest") {
        queryClient.invalidateQueries({
          queryKey: queryKeys.serviceRequests(),
          exact: false,
        });
      }
      // Also invalidate unified requests for any PMS posting update
      queryClient.invalidateQueries({
        queryKey: queryKeys.unifiedRequests(),
        exact: false,
      });
      try {
        // Lazy import to avoid hard dependency if toast not configured here
         
        const { toast } = require("react-toastify");
        if (st === "posted") {
          toast.success(`PMS posted: ${rt} ${resourceId}`);
        } else if (st === "failed") {
          toast.error(lastError || `PMS posting failed: ${rt} ${resourceId}`);
        }
      } catch (error) {
        // Toast not available in this context (e.g., during SSR or before ToastContainer mounted)
        logger.warn("Toast unavailable for PMS posting notification:", error);
      }
    };

    // Chat events
    const handleChatUpdate = () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.chat(),
        exact: false,
      });
    };

    // Sentiment events (payloads may include either qrTokenId only or qrTokenId + roomToken)
    const invalidateSentimentSummary = (qrTokenId?: string, roomToken?: string) => {
      if (qrTokenId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.sentimentSummary(qrTokenId),
        });
      }

      if (roomToken) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.sentimentSummary(roomToken),
        });
      }
    };

    const handleSentimentSummaryUpdated = (qrTokenId: string, roomToken?: string) => {
      invalidateSentimentSummary(qrTokenId, roomToken);
    };

    const handleNegativeSentimentAlert = (qrTokenId: string, ...args: unknown[]) => {
      if (!qrTokenId) {
        return;
      }

      // Backward compatibility:
      // Legacy: (qrTokenId, roomNumber, messagePreview, negativityScore)
      // New:    (qrTokenId, roomToken, roomNumber, messagePreview, negativityScore)
      const roomToken =
        args.length >= 4 && typeof args[0] === "string" ? (args[0] as string) : undefined;

      invalidateSentimentSummary(qrTokenId, roomToken);
    };

    // Express Checkout events removed

    // Register event handlers
    on("ReceiveNewOrderNotification", handleNewOrder);
    on("ReceiveOrderCountUpdate", handleOrderCountUpdate);
    on("ReceiveOrderStatusUpdate", handleOrderStatusUpdate);
    on("ReceiveNewServiceRequestNotification", handleNewServiceRequest);
    on("ReceiveServiceRequestCountUpdate", handleServiceRequestCountUpdate);
    on("ReceiveLateCheckoutNotification", handleNewLateCheckout);
    on("ReceiveLateCheckoutCountUpdate", handleLateCheckoutCountUpdate);
    on("ReceiveNewTransferRequest", handleNewTransferRequest);
    on("ReceiveTransferRequestCountUpdate", handleTransferRequestCountUpdate);
    on("ReceiveNewCheckinNotification", handleNewCheckin);
    on("ReceiveCheckinCountUpdate", handleCheckinCountUpdate);
    on("HousekeepingStatusUpdated", handleHousekeepingUpdate);
    on("ReceiveQueueItemUpdate", handleQueueUpdate);
    on("ReceivePmsPostingUpdate", handlePmsPostingUpdate);
    on("RemoveQueueItem", handleQueueUpdate);
    on("ReceiveMessage", handleChatUpdate);
    on("ReceiveActiveChatSessions", handleChatUpdate);
    on("ReceiveSentimentSummaryUpdated", handleSentimentSummaryUpdated);
    on("ReceiveNegativeSentimentAlert", handleNegativeSentimentAlert);

    // No checkout event handlers to register

    // Cleanup
    return () => {
      if (orderInvalidateTimerRef.current) {
        clearTimeout(orderInvalidateTimerRef.current);
        orderInvalidateTimerRef.current = null;
      }
      off("ReceiveNewOrderNotification", handleNewOrder);
      off("ReceiveOrderCountUpdate", handleOrderCountUpdate);
      off("ReceiveOrderStatusUpdate", handleOrderStatusUpdate);
      off("ReceiveNewServiceRequestNotification", handleNewServiceRequest);
      off("ReceiveServiceRequestCountUpdate", handleServiceRequestCountUpdate);
      off("ReceiveLateCheckoutNotification", handleNewLateCheckout);
      off("ReceiveLateCheckoutCountUpdate", handleLateCheckoutCountUpdate);
      off("ReceiveNewTransferRequest", handleNewTransferRequest);
      off(
        "ReceiveTransferRequestCountUpdate",
        handleTransferRequestCountUpdate,
      );
      off("ReceiveNewCheckinNotification", handleNewCheckin);
      off("ReceiveCheckinCountUpdate", handleCheckinCountUpdate);
      off("HousekeepingStatusUpdated", handleHousekeepingUpdate);
      off("ReceiveQueueItemUpdate", handleQueueUpdate);
      off("ReceivePmsPostingUpdate", handlePmsPostingUpdate);
      off("RemoveQueueItem", handleQueueUpdate);
      off("ReceiveMessage", handleChatUpdate);
      off("ReceiveActiveChatSessions", handleChatUpdate);
      off("ReceiveSentimentSummaryUpdated", handleSentimentSummaryUpdated);
      off("ReceiveNegativeSentimentAlert", handleNegativeSentimentAlert);

      // No checkout event handlers to cleanup
    };
  }, [isConnected, on, off, queryClient, invalidateOrdersDebounced]);
};

/**
 * Hook to use in layout components to enable real-time updates
 */
export const useEnableRealTimeUpdates = () => {
  useSignalRQueryInvalidation();
};
